import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { CheckCircle, XCircle, AlertTriangle, Calendar, MapPin, Clock } from 'lucide-react'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const NAVY  = '#0F1F3D'
const GREEN = '#2A9D5C'

function fmtDate(d) {
  if (!d) return null
  return new Date(d + 'T12:00').toLocaleDateString('en-CA', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  })
}

// ─── PublicStaffResponsePage ──────────────────────────────────────────────────
// Accessible at /staff-respond/:token/:response  (response = 'yes' | 'no')
// No auth required — uses response_token to look up job_staff.

export default function PublicStaffResponsePage() {
  const { token, response } = useParams()
  // response: 'yes' | 'no'

  const [phase,     setPhase]     = useState('loading')  // loading | done | already | error
  const [staffName, setStaffName] = useState('')
  const [job,       setJob]       = useState(null)
  const [prevStatus, setPrevStatus] = useState(null)
  const [err,       setErr]       = useState('')

  useEffect(() => {
    async function process() {
      if (!token) { setErr('Invalid link — no token found.'); setPhase('error'); return }

      const isYes = response === 'yes'
      const isNo  = response === 'no'
      if (!isYes && !isNo) { setErr('Invalid response in link.'); setPhase('error'); return }

      // 1. Fetch the job_staff row + joined user + job
      const { data: row, error: rowErr } = await supabase
        .from('job_staff')
        .select(`
          id, status, user_id, job_id, responded_at,
          users ( full_name, email ),
          jobs (
            id, event_date, setup_date, teardown_date,
            event_type, address,
            estimated_setup_hours, estimated_teardown_hours,
            crew_required, job_notes
          )
        `)
        .eq('response_token', token)
        .single()

      if (rowErr || !row) {
        setErr('This link is invalid or has already expired.')
        setPhase('error')
        return
      }

      setStaffName(row.users?.full_name || 'there')
      setJob(row.jobs)
      setPrevStatus(row.status)

      // 2. If already responded, show the existing result without re-updating
      if (row.status === 'available' || row.status === 'unavailable' || row.status === 'confirmed' || row.status === 'declined') {
        setPhase('already')
        return
      }

      // 3. Record the response
      const newStatus = isYes ? 'available' : 'unavailable'
      const { error: upErr } = await supabase
        .from('job_staff')
        .update({ status: newStatus, responded_at: new Date().toISOString() })
        .eq('response_token', token)

      if (upErr) {
        setErr('Something went wrong recording your response. Please try again or contact the office.')
        setPhase('error')
        return
      }

      setPhase(isYes ? 'yes' : 'no')
    }

    process()
  }, [token, response])

  // ── Shared layout wrapper ────────────────────────────────────────────────

  function Page({ headerColor = NAVY, children }) {
    return (
      <div style={{
        minHeight: '100vh', background: '#f0f2f5',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '40px 16px', fontFamily: 'Arial, Helvetica, sans-serif',
      }}>
        <div style={{
          background: 'white', borderRadius: 12, overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,.12)', width: '100%', maxWidth: 520,
        }}>
          <div style={{ background: headerColor, padding: '26px 32px', color: 'white' }}>
            <div style={{ fontWeight: 700, fontSize: 19 }}>Winnipeg Tent Rental</div>
            <div style={{ opacity: 0.65, fontSize: 13, marginTop: 3 }}>Crew Availability Response</div>
          </div>
          <div style={{ padding: '28px 32px' }}>
            {children}
          </div>
          <div style={{
            background: '#F9FAFB', padding: '13px 32px', borderTop: '1px solid #E5E7EB',
            textAlign: 'center', fontSize: 12, color: '#9CA3AF',
          }}>
            Winnipeg Tent Rental · Winnipeg, MB · info@winnipegtentrentals.ca
          </div>
        </div>
      </div>
    )
  }

  // ── Job details block ────────────────────────────────────────────────────

  function JobDetails() {
    if (!job) return null
    const eventDate    = fmtDate(job.event_date)
    const setupDate    = job.setup_date && job.setup_date !== job.event_date ? fmtDate(job.setup_date) : null
    const teardownDate = job.teardown_date && job.teardown_date !== job.event_date ? fmtDate(job.teardown_date) : null
    const totalHrs     = (parseFloat(job.estimated_setup_hours) || 0) + (parseFloat(job.estimated_teardown_hours) || 0)

    return (
      <div style={{
        background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8,
        padding: '14px 18px', margin: '16px 0', fontSize: 14,
      }}>
        {setupDate && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, color: '#374151' }}>
            <Calendar size={14} style={{ flexShrink: 0, marginTop: 2, color: '#9CA3AF' }} />
            <div><span style={{ color: '#6b7280' }}>Setup: </span>{setupDate}</div>
          </div>
        )}
        {eventDate && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, color: '#374151' }}>
            <Calendar size={14} style={{ flexShrink: 0, marginTop: 2, color: NAVY }} />
            <div><span style={{ color: '#6b7280' }}>Event: </span><strong>{eventDate}</strong></div>
          </div>
        )}
        {teardownDate && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, color: '#374151' }}>
            <Calendar size={14} style={{ flexShrink: 0, marginTop: 2, color: '#9CA3AF' }} />
            <div><span style={{ color: '#6b7280' }}>Teardown: </span>{teardownDate}</div>
          </div>
        )}
        {job.event_type && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, color: '#374151' }}>
            <span style={{ width: 14, flexShrink: 0 }} />
            <div><span style={{ color: '#6b7280' }}>Type: </span>
              {job.event_type.charAt(0).toUpperCase() + job.event_type.slice(1)}
            </div>
          </div>
        )}
        {job.address && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, color: '#374151' }}>
            <MapPin size={14} style={{ flexShrink: 0, marginTop: 2, color: '#9CA3AF' }} />
            <div>{job.address}</div>
          </div>
        )}
        {totalHrs > 0 && (
          <div style={{ display: 'flex', gap: 8, color: '#374151' }}>
            <Clock size={14} style={{ flexShrink: 0, marginTop: 2, color: '#9CA3AF' }} />
            <div>~{totalHrs} hrs estimated ({job.crew_required || 2}-person crew)</div>
          </div>
        )}
      </div>
    )
  }

  // ── Phase renders ────────────────────────────────────────────────────────

  if (phase === 'loading') {
    return (
      <Page>
        <div style={{ textAlign: 'center', padding: '20px 0', color: '#6b7280' }}>Processing your response…</div>
      </Page>
    )
  }

  if (phase === 'error') {
    return (
      <Page>
        <div style={{ textAlign: 'center' }}>
          <AlertTriangle size={40} style={{ color: '#EF4444', marginBottom: 16 }} />
          <div style={{ fontWeight: 700, fontSize: 18, color: NAVY, marginBottom: 10 }}>Link Error</div>
          <div style={{ color: '#6b7280', lineHeight: 1.6, fontSize: 14 }}>{err}</div>
          <div style={{ marginTop: 16, fontSize: 13, color: '#9CA3AF' }}>
            Contact us at <a href="mailto:info@winnipegtentrentals.ca" style={{ color: GREEN }}>
              info@winnipegtentrentals.ca
            </a> if you need help.
          </div>
        </div>
      </Page>
    )
  }

  if (phase === 'already') {
    const wasYes = prevStatus === 'available' || prevStatus === 'confirmed'
    return (
      <Page headerColor={wasYes ? GREEN : '#4B5563'}>
        <div style={{ textAlign: 'center' }}>
          {wasYes ? (
            <CheckCircle size={44} style={{ color: GREEN, marginBottom: 16 }} />
          ) : (
            <XCircle size={44} style={{ color: '#6B7280', marginBottom: 16 }} />
          )}
          <div style={{ fontWeight: 700, fontSize: 19, color: NAVY, marginBottom: 8 }}>
            Already Responded
          </div>
          <div style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.7, marginBottom: 4 }}>
            Hi <strong>{staffName}</strong>, we already have your response for this job:
            <br />
            <strong style={{ color: wasYes ? GREEN : '#4B5563' }}>
              {prevStatus === 'confirmed' ? 'Confirmed — you\'re on the crew'
               : prevStatus === 'available' ? 'Available — awaiting confirmation'
               : prevStatus === 'declined' ? 'Declined'
               : 'Not Available'}
            </strong>
          </div>
          <JobDetails />
          <div style={{ fontSize: 13, color: '#9CA3AF', marginTop: 8 }}>
            Need to change your response? Contact the office directly.
          </div>
        </div>
      </Page>
    )
  }

  if (phase === 'yes') {
    return (
      <Page headerColor={GREEN}>
        <div style={{ textAlign: 'center' }}>
          <CheckCircle size={48} style={{ color: GREEN, marginBottom: 16 }} />
          <div style={{ fontWeight: 700, fontSize: 22, color: NAVY, marginBottom: 8 }}>
            You're Available!
          </div>
          <div style={{ color: '#374151', fontSize: 15, lineHeight: 1.7 }}>
            Thanks, <strong>{staffName}</strong>! We've recorded that you're available for this job.
          </div>
          <JobDetails />
          <div style={{
            background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8,
            padding: '14px 18px', marginTop: 4, fontSize: 14, color: '#15803D', lineHeight: 1.6,
          }}>
            <strong>What's next:</strong> We'll confirm the crew and send you a confirmation email once the team is set. Keep this date open until you hear from us!
          </div>
        </div>
      </Page>
    )
  }

  if (phase === 'no') {
    return (
      <Page headerColor="#4B5563">
        <div style={{ textAlign: 'center' }}>
          <XCircle size={48} style={{ color: '#9CA3AF', marginBottom: 16 }} />
          <div style={{ fontWeight: 700, fontSize: 22, color: NAVY, marginBottom: 8 }}>
            Response Recorded
          </div>
          <div style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.7 }}>
            No problem, <strong>{staffName}</strong>. We've recorded that you're not available for this job. We'll find another crew member for these dates.
          </div>
          <div style={{ marginTop: 20, fontSize: 13, color: '#9CA3AF' }}>
            Changed your mind? Contact us at{' '}
            <a href="mailto:info@winnipegtentrentals.ca" style={{ color: GREEN }}>
              info@winnipegtentrentals.ca
            </a>.
          </div>
        </div>
      </Page>
    )
  }

  return null
}
